import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "task-budget-content" }
const _hoisted_2 = { class: "task-title" }
const _hoisted_3 = { class: "task-filters" }
const _hoisted_4 = { class: "filter-item" }
const _hoisted_5 = { class: "filter-label" }
const _hoisted_6 = { class: "task-info" }
const _hoisted_7 = { class: "task-name-wrapper" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = {
  key: 0,
  class: "task-description"
}
const _hoisted_10 = { class: "budget-cell" }
const _hoisted_11 = { class: "currency-amount positive-amount budget-amount" }
const _hoisted_12 = {
  key: 0,
  style: {"color":"gray","font-size":"10px"}
}
const _hoisted_13 = { class: "budget-cell" }
const _hoisted_14 = { class: "currency-amount negative-amount consumed-amount" }
const _hoisted_15 = {
  key: 0,
  style: {"color":"gray","font-size":"10px"}
}
const _hoisted_16 = { class: "budget-cell" }
const _hoisted_17 = {
  key: 0,
  style: {"color":"gray","font-size":"10px"}
}
const _hoisted_18 = { class: "consumption-cell" }
const _hoisted_19 = {
  key: 0,
  class: "over-consumption-indicator"
}
const _hoisted_20 = { class: "intervenant-cell" }
const _hoisted_21 = ["onClick"]
const _hoisted_22 = {
  key: 2,
  class: "no-intervenant"
}
const _hoisted_23 = { class: "dates-info" }
const _hoisted_24 = { class: "date-item start-date" }
const _hoisted_25 = { class: "date-content" }
const _hoisted_26 = { class: "date-item end-date" }
const _hoisted_27 = { class: "date-content" }
const _hoisted_28 = { class: "expenses-info" }
const _hoisted_29 = { class: "expense-count" }
const _hoisted_30 = { class: "count" }
const _hoisted_31 = { class: "label" }
const _hoisted_32 = {
  key: 0,
  class: "expense-dates"
}

import { formatDate, formatPercentage } from '@/utils/formatters';
import { formatCurrencyWithUserPreference } from '@/utils/currencyUtils';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ProgressBar from 'primevue/progressbar';
import Avatar from 'primevue/avatar';
import CustomDropdown from '@/components/common/CustomDropdown.vue';
import PaginationControls from '@/components/common/PaginationControls.vue';
// @ts-ignore
import { ref, computed, watch, nextTick } from 'vue';

interface Props {
  tasks?: any[];
  loading?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TaskBudgetReport',
  props: {
    tasks: {},
    loading: { type: Boolean }
  },
  emits: ['task-click', 'intervenant-click'],
  setup(__props: any) {

const props = __props;



// State
const selectedIntervenant = ref<number | null>(null);
const formattedBudgets = ref<Record<string, string>>({} as Record<string, string>);
const formattedConsumed = ref<Record<string, string>>({} as Record<string, string>);
const formattedRemaining = ref<Record<string, string>>({} as Record<string, string>);
const tableKey = ref<number>(0); // For forcing re-render

// Pagination state
const currentPage = ref<number>(1);
const pageSize = ref<number>(10);

// Format currency amounts when tasks change
const formatTaskAmounts = async () => {
  if (!props.tasks?.length) return;

  const budgets: Record<string, string> = {};
  const consumed: Record<string, string> = {};
  const remaining: Record<string, string> = {};

  for (const task of props.tasks) {
    if (task.taskId) {
      try {
        budgets[String(task.taskId)] = await formatCurrencyWithUserPreference(task.budgetAmount);
        consumed[String(task.taskId)] = await formatCurrencyWithUserPreference(task.consumedAmount);
        remaining[String(task.taskId)] = await formatCurrencyWithUserPreference(task.remainingAmount);
      } catch (err) {
        console.warn('Error formatting currency for task:', task.taskId, err);
        budgets[String(task.taskId)] = task.budgetAmount?.toString() || '0';
        consumed[String(task.taskId)] = task.consumedAmount?.toString() || '0';
        remaining[String(task.taskId)] = task.remainingAmount?.toString() || '0';
      }
    }
  }

  formattedBudgets.value = budgets;
  formattedConsumed.value = consumed;
  formattedRemaining.value = remaining;
  
  // Force table re-render to update currency display
  await nextTick();
  tableKey.value++;
};

// Watch for tasks changes
watch(() => props.tasks, formatTaskAmounts, { immediate: true, deep: true });

// Computed properties
const intervenantOptions = computed(() => {
  const intervenants = [...new Set((props.tasks || [])
    .filter((task: any) => task.intervenantName)
    .map((task: any) => ({ id: task.intervenantId, name: task.intervenantName }))
  )];
  return intervenants.filter((intervenant: any, index: number, self: any[]) => 
    index === self.findIndex((i: any) => i.id === intervenant.id)
  );
});

const filteredTasks = computed(() => {
  let filtered = [...(props.tasks || [])];

  if (selectedIntervenant.value) {
    filtered = filtered.filter((task: any) => task.intervenantId === selectedIntervenant.value);
  }

  return filtered;
});

const paginatedTasks = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  return filteredTasks.value.slice(start, end);
});

// Methods
const applyFilters = () => {
  // Reset to first page when filters change
  currentPage.value = 1;
};

const getProgressBarClass = (percentage: number) => {
  if (percentage >= 100) return 'progress-over';
  if (percentage >= 80) return 'progress-high';
  if (percentage >= 60) return 'progress-medium';
  return 'progress-low';
};

const getPercentageClass = (percentage: number) => {
  if (percentage >= 100) return 'over-budget';
  if (percentage >= 80) return 'high-usage';
  if (percentage >= 60) return 'medium-usage';
  return 'low-usage';
};

const getInitials = (name: string) => {
  return name.split(' ').map((word: string) => word.charAt(0)).join('').toUpperCase().substring(0, 2);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("h2", _hoisted_2, [
      _cache[2] || (_cache[2] = _createElementVNode("i", { class: "pi pi-file-o" }, null, -1)),
      _createTextVNode(" " + _toDisplayString(_ctx.$t('reports.taskBudgetReport')), 1)
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("label", _hoisted_5, [
          _cache[3] || (_cache[3] = _createElementVNode("i", { class: "pi pi-users" }, null, -1)),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('reports.filterByIntervenant')), 1)
        ]),
        _createVNode(CustomDropdown, {
          modelValue: selectedIntervenant.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedIntervenant).value = $event)),
          options: intervenantOptions.value,
          optionLabel: "name",
          optionValue: "id",
          placeholder: _ctx.$t('reports.selectIntervenant'),
          onChange: applyFilters,
          showClear: ""
        }, null, 8, ["modelValue", "options", "placeholder"])
      ])
    ]),
    (_openBlock(), _createBlock(_unref(DataTable), {
      key: tableKey.value,
      value: paginatedTasks.value,
      sortMode: "multiple",
      removableSort: "",
      loading: _ctx.loading,
      responsiveLayout: "scroll",
      class: "task-table",
      globalFilterFields: ['taskName', 'intervenantName']
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Column), {
          field: "taskName",
          header: _ctx.$t('tasks.name'),
          sortable: ""
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("span", {
                  class: "task-name",
                  onClick: ($event: any) => (_ctx.$emit('task-click', data))
                }, [
                  _cache[4] || (_cache[4] = _createElementVNode("i", { class: "pi pi-file-o task-icon" }, null, -1)),
                  _createTextVNode(" " + _toDisplayString(data.taskName), 1)
                ], 8, _hoisted_8)
              ]),
              (data.taskDescription)
                ? (_openBlock(), _createElementBlock("small", _hoisted_9, _toDisplayString(data.taskDescription), 1))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        }, 8, ["header"]),
        _createVNode(_unref(Column), {
          field: "budgetAmount",
          header: _ctx.$t('reports.budget.allocated'),
          sortable: ""
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("span", _hoisted_11, [
                _createTextVNode(_toDisplayString(formattedBudgets.value[data.taskId] || data.budgetAmount || '0') + " ", 1),
                (!formattedBudgets.value[data.taskId])
                  ? (_openBlock(), _createElementBlock("small", _hoisted_12, "(raw)"))
                  : _createCommentVNode("", true)
              ]),
              _cache[5] || (_cache[5] = _createElementVNode("div", { class: "budget-indicator allocated" }, null, -1))
            ])
          ]),
          _: 1
        }, 8, ["header"]),
        _createVNode(_unref(Column), {
          field: "consumedAmount",
          header: _ctx.$t('reports.consumed'),
          sortable: ""
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", _hoisted_13, [
              _createElementVNode("span", _hoisted_14, [
                _createTextVNode(_toDisplayString(formattedConsumed.value[data.taskId] || data.consumedAmount || '0') + " ", 1),
                (!formattedConsumed.value[data.taskId])
                  ? (_openBlock(), _createElementBlock("small", _hoisted_15, "(raw)"))
                  : _createCommentVNode("", true)
              ]),
              _cache[6] || (_cache[6] = _createElementVNode("div", { class: "budget-indicator consumed" }, null, -1))
            ])
          ]),
          _: 1
        }, 8, ["header"]),
        _createVNode(_unref(Column), {
          field: "remainingAmount",
          header: _ctx.$t('reports.remaining'),
          sortable: ""
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", _hoisted_16, [
              _createElementVNode("span", {
                class: _normalizeClass([
                  'currency-amount',
                  'remaining-amount',
                  data.remainingAmount >= 0 ? 'positive-amount' : 'negative-amount'
                ])
              }, [
                _createTextVNode(_toDisplayString(formattedRemaining.value[data.taskId] || data.remainingAmount || '0') + " ", 1),
                (!formattedRemaining.value[data.taskId])
                  ? (_openBlock(), _createElementBlock("small", _hoisted_17, "(raw)"))
                  : _createCommentVNode("", true)
              ], 2),
              _createElementVNode("div", {
                class: _normalizeClass(["budget-indicator remaining", { 'negative': data.remainingAmount < 0 }])
              }, null, 2)
            ])
          ]),
          _: 1
        }, 8, ["header"]),
        _createVNode(_unref(Column), {
          field: "consumptionPercentage",
          header: _ctx.$t('reports.consumptionPercentage'),
          sortable: ""
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", _hoisted_18, [
              _createVNode(_unref(ProgressBar), {
                value: Math.min(data.consumptionPercentage, 100),
                class: _normalizeClass([getProgressBarClass(data.consumptionPercentage), "consumption-bar modern-progress"])
              }, null, 8, ["value", "class"]),
              _createElementVNode("span", {
                class: _normalizeClass(["consumption-text", getPercentageClass(data.consumptionPercentage)])
              }, _toDisplayString(_unref(formatPercentage)(data.consumptionPercentage)), 3),
              (data.consumptionPercentage > 100)
                ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                    _cache[7] || (_cache[7] = _createElementVNode("i", { class: "pi pi-exclamation-triangle" }, null, -1)),
                    _createTextVNode(" +" + _toDisplayString(_unref(formatPercentage)(data.consumptionPercentage - 100)), 1)
                  ]))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        }, 8, ["header"]),
        _createVNode(_unref(Column), {
          field: "intervenantName",
          header: _ctx.$t('intervenants.name'),
          sortable: ""
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", _hoisted_20, [
              (data.intervenantName)
                ? (_openBlock(), _createBlock(_unref(Avatar), {
                    key: 0,
                    label: getInitials(data.intervenantName),
                    shape: "circle",
                    size: "small",
                    class: "intervenant-avatar"
                  }, null, 8, ["label"]))
                : _createCommentVNode("", true),
              (data.intervenantName)
                ? (_openBlock(), _createElementBlock("span", {
                    key: 1,
                    class: "intervenant-name",
                    onClick: ($event: any) => (_ctx.$emit('intervenant-click', { id: data.intervenantId, name: data.intervenantName }))
                  }, _toDisplayString(data.intervenantName), 9, _hoisted_21))
                : (_openBlock(), _createElementBlock("span", _hoisted_22, [
                    _cache[8] || (_cache[8] = _createElementVNode("i", { class: "pi pi-user-minus" }, null, -1)),
                    _createTextVNode(" " + _toDisplayString(_ctx.$t('reports.notAssigned')), 1)
                  ]))
            ])
          ]),
          _: 1
        }, 8, ["header"]),
        _createVNode(_unref(Column), {
          header: _ctx.$t('reports.dates'),
          sortable: ""
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", _hoisted_23, [
              _createElementVNode("div", _hoisted_24, [
                _cache[9] || (_cache[9] = _createElementVNode("i", { class: "pi pi-calendar-plus" }, null, -1)),
                _createElementVNode("div", _hoisted_25, [
                  _createElementVNode("small", null, _toDisplayString(_ctx.$t('tasks.startDate')), 1),
                  _createElementVNode("span", null, _toDisplayString(_unref(formatDate)(data.startDate) || '-'), 1)
                ])
              ]),
              _createElementVNode("div", _hoisted_26, [
                _cache[10] || (_cache[10] = _createElementVNode("i", { class: "pi pi-calendar-minus" }, null, -1)),
                _createElementVNode("div", _hoisted_27, [
                  _createElementVNode("small", null, _toDisplayString(_ctx.$t('tasks.endDate')), 1),
                  _createElementVNode("span", null, _toDisplayString(_unref(formatDate)(data.endDate) || '-'), 1)
                ])
              ])
            ])
          ]),
          _: 1
        }, 8, ["header"]),
        _createVNode(_unref(Column), {
          header: _ctx.$t('reports.expenses'),
          sortable: ""
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", _hoisted_28, [
              _createElementVNode("div", _hoisted_29, [
                _cache[11] || (_cache[11] = _createElementVNode("i", { class: "pi pi-receipt" }, null, -1)),
                _createElementVNode("span", _hoisted_30, _toDisplayString(data.expenseCount), 1),
                _createElementVNode("span", _hoisted_31, _toDisplayString(_ctx.$t('reports.expenses')), 1)
              ]),
              (data.firstExpenseDate)
                ? (_openBlock(), _createElementBlock("div", _hoisted_32, [
                    _cache[12] || (_cache[12] = _createElementVNode("i", { class: "pi pi-clock" }, null, -1)),
                    _createElementVNode("small", null, _toDisplayString(_unref(formatDate)(data.firstExpenseDate)) + " - " + _toDisplayString(_unref(formatDate)(data.lastExpenseDate)), 1)
                  ]))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        }, 8, ["header"])
      ]),
      _: 1
    }, 8, ["value", "loading"])),
    _createVNode(PaginationControls, {
      currentPage: currentPage.value,
      "onUpdate:currentPage": _cache[1] || (_cache[1] = ($event: any) => ((currentPage).value = $event)),
      pageSize: pageSize.value,
      totalItems: filteredTasks.value.length,
      itemsLabel: _ctx.$t('reports.tasks.title').toLowerCase()
    }, null, 8, ["currentPage", "pageSize", "totalItems", "itemsLabel"])
  ]))
}
}

})